import { BETRIEB } from '@malerbetrieb/shared';
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '../stores/auth.js';

/**
 * Routen in deutscher Schreibweise – die Adresszeile ist Teil der
 * Oberfläche und soll für die Anwender lesbar sein.
 */
const routes: RouteRecordRaw[] = [
  {
    path: '/anmelden',
    name: 'anmelden',
    component: () => import('../views/LoginView.vue'),
    meta: { oeffentlich: true, titel: 'Anmelden' },
  },
  {
    path: '/',
    name: 'auftraege',
    component: () => import('../views/OrderListView.vue'),
    meta: { titel: 'Aufträge' },
  },
  {
    path: '/auftraege/neu',
    name: 'auftrag-neu',
    component: () => import('../views/OrderFormView.vue'),
    meta: { titel: 'Neuer Auftrag' },
  },
  {
    path: '/auftraege/:orderId',
    name: 'auftrag',
    component: () => import('../views/OrderDetailView.vue'),
    props: true,
    meta: { titel: 'Auftrag' },
  },
  {
    path: '/auftraege/:orderId/bearbeiten',
    name: 'auftrag-bearbeiten',
    component: () => import('../views/OrderFormView.vue'),
    props: true,
    meta: { titel: 'Auftrag bearbeiten' },
  },
  {
    path: '/auftraege/:orderId/positionen/:positionId',
    name: 'position',
    component: () => import('../views/PositionEditView.vue'),
    props: true,
    meta: { titel: 'Position' },
  },
  {
    path: '/auftraege/:orderId/abschluss',
    name: 'abschluss',
    component: () => import('../views/FinishView.vue'),
    props: true,
    meta: { titel: 'Aufnahme prüfen' },
  },
  {
    path: '/konflikte',
    name: 'konflikte',
    component: () => import('../views/ConflictsView.vue'),
    meta: { titel: 'Konflikte' },
  },
  {
    path: '/einstellungen',
    name: 'einstellungen',
    component: () => import('../views/SettingsView.vue'),
    meta: { titel: 'Einstellungen' },
  },
  {
    path: '/:pfad(.*)*',
    name: 'nicht-gefunden',
    component: () => import('../views/NotFoundView.vue'),
    meta: { titel: 'Nicht gefunden' },
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, saved) {
    return saved ?? { top: 0 };
  },
});

/**
 * Zugangsschutz.
 *
 * Maßgeblich ist der lokal bekannte Benutzer, nicht die Serversitzung –
 * sonst wäre die App im Funkloch nicht bedienbar. Die eigentliche
 * Rechteprüfung findet ohnehin serverseitig bei jedem Zugriff statt.
 */
router.beforeEach(async (to) => {
  const auth = useAuthStore();

  if (auth.status === 'unbekannt') await auth.starten();

  if (to.meta.oeffentlich) {
    if (auth.istAngemeldet && auth.status !== 'sitzung-abgelaufen') return { name: 'auftraege' };
    return true;
  }

  if (!auth.istAngemeldet) {
    return { name: 'anmelden', query: { weiter: to.fullPath } };
  }

  return true;
});

router.afterEach((to) => {
  const titel = typeof to.meta.titel === 'string' ? to.meta.titel : 'Baustellenaufnahme';
  document.title = `${titel} · ${BETRIEB.name}`;
});
